import { Button, Tooltip } from '@gisce/oficina-virtual-components';
import {
  Card, CardContent, CardActions, 
  Typography, Box, CircularProgress,
  Grid, Alert
} from '@mui/material';
import { 
  Feedback, GetApp, Visibility, 
  Receipt, AccountBalance, Power,
  Discount,
  CreateOutlined
} from '@mui/icons-material';

import { useSettingsContext } from '@/components/Settings/hooks/useSettingsContext';
import useAuth from '@/components/Authentication/hooks/useAuth';

import TextFieldReadOnly from '@/components/TextFieldReadOnly/TextFieldReadOnly';
import { useTranslation } from 'react-i18next';
import { ContractModificationTypes } from '../../types/contracts';
import useLangs from '@/languages/hooks/useLangs';
import { NoSIPSCessionAction } from './components/NoSIPSCessionAction';
import { useContracts } from '../../hooks/useContracts';
import CustomizeDoubleTariff from './components/CustomizeDoubleTariff';
import CustomizeHappyHours from './components/CustomizeHappyHours';
import CustomizeHappyDay from './components/CustomizeHappyDay';
import { FC } from 'react';
import useIdNavigate from '@/hooks/useIdNavigate';
import ContractElectronicNotification from './components/ElectronicNotificationAction';

interface ContractDetailsPanelProps {
  openDialog: (type: ContractModificationTypes) => void;
}

export const ElecContractDetailsPanel: FC<ContractDetailsPanelProps> = ({
  openDialog
}) => {

  const { t } = useTranslation();
  const { scrollToSection } = useIdNavigate();
  const { currentLang } = useLangs();
  const { tokenVals } = useAuth();
  const { 
    goToFilteredInvoices, 
    fetchContracts, 
    currentContract: contract, 
    downloadContract,
    downloadingContract
  } = useContracts("electricity");
  const Settings = useSettingsContext();
  
  const tokenReadOnly = tokenVals?.readonly;

  return <>

    {contract.state === "baixa" &&
      <Alert severity="warning" sx={{mb: 2}}>
        {t("common:text.contract_disabled_cant_modify")}
      </Alert>
    }

    <Card sx={{marginBottom: 3}}>
      <CardContent>
        <Typography variant="h5">
          {t('common:text.contract_data_contract')}
        </Typography>
        {/* <Chip 
          label={(contract.state == "baixa" || contract.state == "cancelada") ? "Baixa" : "Activa"} 
          color={(contract.state == "baixa" || contract.state == "cancelada") ? "warning" : "default"} 
        /> */}
        {Settings.features?.noCesionSips && <div>
          <NoSIPSCessionAction
            noSIPSCession={contract.no_cession_sips}
            isAtrAvailable={contract.atr_available}
            isContactActive={contract.state === 'activa'}
            dialogOpen={() => {openDialog("noSIPSCession")}}
          />
        </div>}
        <Grid container>
          <Grid item xs={12} md={6}>
            <TextFieldReadOnly
              label={t('common:text.contract_owner')}
            >
              {contract?.owner_partner.name}
            </TextFieldReadOnly>
          </Grid>
          <Grid item xs={12} md={6}>
            <TextFieldReadOnly
              label={t('common:text.contract_payer')}
            >
              {contract?.bank_account?.owner?.name}
            </TextFieldReadOnly>
          </Grid>
          <Grid item xs={12} md={6}>
            <TextFieldReadOnly label={t('common:text.contract_contracted_power')}>
              { contract.contracted_power_period && 
                contract.contracted_power_period.length &&
                contract.contracted_power_period.reduce(
                  (accumulator, currentValue) => {
                    const currPower = currentValue.power.toLocaleString(currentLang);
                    return `${accumulator} ${currentValue.period}: ${currPower} kW`
                  }, '')
              }

              {Settings.features?.powerChange && 
                !Settings?.contract?.horizontalButtons &&
                contract.state === 'activa' && 
                <Tooltip
                  title={!!tokenReadOnly &&
                    t("common:text.token_readonly_tooltip")
                  }
                  placement="right"
                  arrow
                  disableFocusListener={!tokenReadOnly}
                  disableHoverListener={!tokenReadOnly}
                  disableTouchListener={!tokenReadOnly}
                  slotProps={{
                    popper: {
                      modifiers: [{
                        name: 'offset',
                        options: {offset: [0, -5]}
                      }]
                    }
                  }}
                >
                  <span style={{ 'width': '100%' }}>
                    <Button
                      variant={'text'}
                      color={'primary'}
                      disabled={
                        !contract.atr_available
                        || contract.waitingForSignature?.includes("power")
                        || !!tokenReadOnly}
                      title={t('common:text.contract_power_modification')}
                      onClick={() => openDialog("power")}
                    >
                      <CreateOutlined style={{marginRight: 10}}/>
                    </Button>
                  </span>
                </Tooltip>
              }
              {(!contract?.atr_available || contract.waitingForSignature?.includes("power")) &&
                <div style={{fontSize: '12px'}}>
                  <em>{t('common:text.contract_process_currently')}</em>
                </div>
              }
            </TextFieldReadOnly>
          </Grid>
          <Grid item xs={12} md={6}>
            <div>
              {/* Si no te nom comercial llista de preus {_.get(contract, 'pricelist.name', '')} */}
              <TextFieldReadOnly label={t('common:text.contract_contracted_product')}>
                {contract?.pricelist.nom_comercial ?
                  contract?.pricelist?.nom_comercial : contract?.pricelist?.name
                }
              </TextFieldReadOnly>

              {Settings.contract?.customizeDoubleTariff && <CustomizeDoubleTariff
                contractId={contract.name}
                onUpdate={()=> fetchContracts(false)}
              />}
              {Settings.contract?.customizeHappyHours && <CustomizeHappyHours
                contractId={contract.name}
                onUpdate={()=> fetchContracts(false)}
              />}
              {Settings.contract?.customizeHappyDay && <CustomizeHappyDay
                contractId={contract.name}
                onUpdate={()=> fetchContracts(false)}
              />}
            </div>
          </Grid>
          <Grid item xs={12} md={6}>
            <TextFieldReadOnly 
              label={t('common:text.contract_payment_account')}
            >
              {contract?.bank_account ? 
                contract.bank_account.printable_iban 
                :
                t('common:text.contract_no_domiciled')
              }
              {Settings.contract?.ibanChange?.enabled &&
                !Settings?.contract?.horizontalButtons &&
                contract.state === 'activa' &&
                <Tooltip
                  title={!!tokenReadOnly &&
                    t("common:text.token_readonly_tooltip")
                  }
                  placement="right"
                  arrow
                  disableFocusListener={!tokenReadOnly}
                  disableHoverListener={!tokenReadOnly}
                  disableTouchListener={!tokenReadOnly}
                  slotProps={{
                    popper: {
                      modifiers: [{
                        name: 'offset',
                        options: {offset: [0, -5]}
                      }]
                    }
                  }}
                >
                  <span style={{ 'width': '100%' }}>
                    <Button
                      variant={'text'}
                      color={'primary'}
                      disabled={!!tokenReadOnly || contract.waitingForSignature?.includes("iban")}
                      title={t('common:text.contract_account_modification')}
                      onClick={() => openDialog("iban")}
                    >
                      {contract.bank_account ?
                        <CreateOutlined style={{marginRight: 10}}/>
                        : t('common:text.contract_enable_domiciliation')}
                    </Button>
                  </span>
                </Tooltip>
              }
              {contract.waitingForSignature?.includes("iban") &&
                <div style={{fontSize: '12px'}}>
                  <em>{t('common:text.contract_active_signature_proces')}</em>
                </div>
              }
            </TextFieldReadOnly>
          </Grid>
          <Grid item xs={12} md={6}>
            <TextFieldReadOnly
              label={t('common:text.contract_nif')}
            >
              {contract?.fiscal_partner.vat}
            </TextFieldReadOnly>
          </Grid>
          <Grid item xs={12} md={6}>
            <TextFieldReadOnly
              label={t('common:text.contract_fiscal_address')}
            >
              {contract?.fiscal_address?.full_address}
            </TextFieldReadOnly>
          </Grid>
          {Settings?.contract?.refCatastral?.enabled &&
            <Grid item xs={12} md={6}>
              <TextFieldReadOnly label={t('common:text.contract_ref_catastral')}>
                {contract.ref_catastral}

                {Settings?.contract?.refCatastral?.modify &&
                  contract.state === 'activa' && 
                <Button
                  variant={'text'}
                  color={'primary'}
                  onClick={() => openDialog("refCatastral")}
                >
                  {contract.ref_catastral !== "" ?
                    t('common:text.contract_modification')
                    :
                    t('common:text.contract_add_ref_catastral')
                  }
                </Button>
                }
              </TextFieldReadOnly>
            </Grid>
          }
          {Settings.features.modifyElectronicNotification &&
            contract.type === "electricity" &&
            <ContractElectronicNotification contract={contract} />
          }
        </Grid>
      </CardContent>
      <CardActions>
        <Box flexWrap="wrap" px={1} pb={1}>
          { !Settings.features?.disableDownloadContractReport &&
            <Button
              key={'download'}
              variant={'contained'}
              color={'primary'}
              onClick={() => downloadContract()}
              disabled={downloadingContract}
              style={{marginRight: 12, marginTop: 8}}
            >
              {(downloadingContract ? <CircularProgress size={25} style={{marginRight: 10}} /> :
                <GetApp style={{marginRight: 10}}/>)}
              {t('common:text.contract_download')}
            </Button>
          }
          <Button
            key={'invoices'}
            variant={'contained'}
            color={'primary'}
            onClick={() => goToFilteredInvoices(contract)}
            title={t('common:text.contract_related_invoices')}
            style={{marginRight: 12, marginTop: 8}}
          >
            {<Receipt style={{marginRight: 10}}/>}
            {t('common:text.contracts_view_invoices_button_button')}
          </Button>

          {Settings.features?.reclamATCContract &&
            <Button
              key={'reclamation'}
              variant={'contained'}
              color={'primary'}
              onClick={() => openDialog("reclam")}
              style={{marginRight: 12, marginTop: 8}}
            >
              {<Feedback style={{marginRight: 10}}/>}
              {t('common:text.contract_create_atc_reclamation')}
            </Button>
          }
          {Settings.features?.powerChange && 
            Settings?.contract?.horizontalButtons &&
            contract.state === 'activa' && 
            <Tooltip
              title={!!tokenReadOnly &&
                t("common:text.token_readonly_tooltip")
              }
              placement="bottom"
              arrow
              disableFocusListener={!tokenReadOnly}
              disableHoverListener={!tokenReadOnly}
              disableTouchListener={!tokenReadOnly}
              slotProps={{
                popper: {
                  modifiers: [{
                    name: 'offset',
                    options: {offset: [0, 10]}
                  }]
                }
              }}
            >
              <span style={{ 'width': '100%' }}>
                <Button
                  key={'changePower'}
                  variant={'contained'}
                  color={'primary'}
                  disabled={!contract.atr_available || !!tokenReadOnly}
                  title={t('common:text.contract_power_modification')}
                  onClick={() => openDialog("power")}
                  style={{marginRight: 12, marginTop: 8}}
                >
                  {<Power style={{marginRight: 10}}/>}
                  {t('common:text.contract_power_modification')}
                </Button>
              </span>
            </Tooltip>
          }
          {Settings.features?.noCesionSips && 
            Settings?.contract?.horizontalButtons &&
            contract.state === 'activa' && 
            <Tooltip
              title={!!tokenReadOnly &&
                t("common:text.token_readonly_tooltip")
              }
              placement="bottom"
              arrow
              disableFocusListener={!tokenReadOnly}
              disableHoverListener={!tokenReadOnly}
              disableTouchListener={!tokenReadOnly}
              slotProps={{
                popper: {
                  modifiers: [{
                    name: 'offset',
                    options: {offset: [0, 10]}
                  }]
                }
              }}
            >
              <span style={{ 'width': '100%' }}>
                <Button
                  key={'sips'}
                  variant={'contained'}
                  color={'primary'}
                  disabled={!contract.atr_available || contract.no_cession_sips.state === 'active' || !!tokenReadOnly}
                  title={t('common:text.sips_title')}
                  onClick={() => openDialog("noSIPSCession")}
                  style={{marginRight: 12, marginTop: 8}}
                >
                  {<Visibility style={{marginRight: 10}}/>}
                  {t('common:text.sips_title')}
                </Button>
              </span>
            </Tooltip>
          }
          { Settings.contract?.ibanChange?.enabled &&
            Settings?.contract?.horizontalButtons &&
            contract.bank_account &&
            contract.state === 'activa' && 
            <Tooltip
              title={!!tokenReadOnly &&
                t("common:text.token_readonly_tooltip")
              }
              placement="bottom"
              arrow
              disableFocusListener={!tokenReadOnly}
              disableHoverListener={!tokenReadOnly}
              disableTouchListener={!tokenReadOnly}
              slotProps={{
                popper: {
                  modifiers: [{
                    name: 'offset',
                    options: {offset: [0, 10]}
                  }]
                }
              }}
            >
              <span style={{ 'width': '100%' }}>
                <Button
                  key={'iban'}
                  variant={'contained'}
                  color={'primary'}
                  disabled={!!tokenReadOnly || contract?.bank_account?.active_signature_proces}
                  title={ contract.bank_account ?
                    t('common:text.contract_account_modification') 
                    : t('common:text.contract_enable_domiciliation')
                  }
                  onClick={() => openDialog("iban")}
                  style={{marginRight: 12, marginTop: 8}}
                >
                  {<AccountBalance style={{marginRight: 10}}/>}
                  {contract.bank_account ?
                    t('common:text.contract_account_modification') 
                    : t('common:text.contract_enable_domiciliation')}
                </Button>
              </span>
            </Tooltip>
          }
          { Settings.features.promotionalCode?.contractDetails?.applyCoupon &&
            contract.state === 'activa' && 
            <Tooltip
              title={!!tokenReadOnly &&
                t("common:text.token_readonly_tooltip")
              }
              placement="bottom"
              arrow
              disableFocusListener={!tokenReadOnly}
              disableHoverListener={!tokenReadOnly}
              disableTouchListener={!tokenReadOnly}
              slotProps={{
                popper: {
                  modifiers: [{
                    name: 'offset',
                    options: {offset: [0, 10]}
                  }]
                }
              }}
            >
              <span style={{ 'width': '100%' }}>
                <Button
                  key={'applyPromocode'}
                  variant={'contained'}
                  color={'primary'}
                  disabled={!!tokenReadOnly}
                  title={t("common:text.contracts_add_promocode")}
                  onClick={() => {scrollToSection("coupon-list")}}
                  style={{marginRight: 12, marginTop: 8}}
                  startIcon={<Discount />}
                >
                  {t("common:text.contracts_add_promocode")}
                </Button>
              </span>
            </Tooltip>
          }
        </Box>
      </CardActions>
    </Card>
  </>
}
